import React from 'react';
import { Check, XCircle, Circle } from 'lucide-react';
import {
    PIPELINE_STAGE_ORDER, PIPELINE_STAGE_LABELS, PIPELINE_STAGE_PIVOT,
    PipelineStage, isTerminalStage, stageRank,
} from './pipelineStages';

const fmtJour = (d?: string | null) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); } catch { return null; }
};

/**
 * La frise des stades — où en est le dossier, d'un coup d'œil.
 *
 * Les dates viennent des horodatages réellement posés par les transitions, jamais du journal : le journal
 * raconte l'histoire (y compris les allers-retours), la frise dit l'état.
 *
 * Le jalon pivot (« Devis signé ») est marqué visuellement : c'est lui qui commande tout le reste, rien de
 * la production ne démarre avant lui.
 */
export function PipelineFrise({ stage, dates }: {
    stage: PipelineStage;
    dates: Partial<Record<PipelineStage, string | null>>;
}) {
    const courant = stageRank(stage);
    const abandonne = isTerminalStage(stage);

    return (
        <div className="space-y-1">
            {abandonne && (
                <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-500/10 border border-red-500/30 mb-3">
                    <XCircle size={15} className="text-red-400 shrink-0" />
                    <p className="text-xs font-bold text-red-400">
                        {PIPELINE_STAGE_LABELS[stage]} — le parcours s'arrête ici.
                    </p>
                </div>
            )}
            <ol className="space-y-0">
                {PIPELINE_STAGE_ORDER.map((s, i) => {
                    // Sur un dossier abandonné, on fige la frise là où il en était : tout marquer comme non
                    // atteint effacerait le chemin déjà parcouru, qui est justement ce qu'on vient lire.
                    const franchi = !abandonne && i < courant;
                    const actif = !abandonne && i === courant;
                    const pivot = s === PIPELINE_STAGE_PIVOT;
                    const date = fmtJour(dates[s]);
                    const dernier = i === PIPELINE_STAGE_ORDER.length - 1;
                    return (
                        <li key={s} className="flex items-stretch gap-3">
                            <div className="flex flex-col items-center">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 border ${
                                    franchi ? 'bg-brand-600 border-brand-600 text-brand-fg'
                                        : actif ? 'bg-brand-500/15 border-brand-500 text-brand-600 dark:text-brand-400'
                                            : 'bg-surface border-border text-content-muted'}`}>
                                    {franchi ? <Check size={11} strokeWidth={3} /> : <Circle size={7} fill="currentColor" />}
                                </span>
                                {!dernier && <span className={`w-px flex-1 min-h-4 ${franchi ? 'bg-brand-600' : 'bg-border'}`} />}
                            </div>
                            <div className={`pb-3 min-w-0 ${dernier ? 'pb-0' : ''}`}>
                                <p className={`text-xs leading-5 ${actif ? 'font-black text-content' : franchi ? 'font-semibold text-content' : 'text-content-muted'}`}>
                                    {PIPELINE_STAGE_LABELS[s]}
                                    {pivot && (
                                        <span className="ml-1.5 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                                            jalon
                                        </span>
                                    )}
                                </p>
                                {date && <p className="text-[10px] text-content-muted">{date}</p>}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
